// The loop family's two billing-ownership refusals (#7662) — split out of `loop.ts`
// purely for size (biome's 800-line file cap), the `commerce-webhook-source.ts`
// posture: this group is spread into `TERMINAL_RENDERERS_LOOP`, so the family's
// partition and coverage tests see it where the vocabulary
// (`../../audit/kind-loop.ts`, beside `account.seats_synced`) puts it.
//
// Both are `warn`, never `'omit'`: each is a Stripe subscription the customer pays
// for that changed nothing — a second live seat subscription (a double charge an
// operator has to refund), or a failed invoice that may not pause the account (a
// donor fee's). Written by the billing transition driver's final transaction
// (`apps/worker/src/consumers/stripe-events-transitions.ts`).

import type {
  TerminalRenderContext,
  TerminalRendered,
  TerminalRow,
  TerminalSubject,
} from '../../../wire/terminal';
import { terminalRendererGroup } from '../../../wire/terminal';

function inField(row: TerminalRow, key: string): string {
  const input = row.input;
  if (typeof input !== 'object' || input === null) return 'unknown';
  const v = (input as Record<string, unknown>)[key];
  return typeof v === 'string' && v.length > 0 ? v : 'unknown';
}

/** The row's repo when it has one, else its session — the loop family's fallback. */
function subjectForRow(row: TerminalRow): TerminalSubject {
  if (row.repoFullName !== null) {
    return { kind: 'repo', id: row.repoFullName, label: row.repoFullName };
  }
  return { kind: 'session', id: row.sessionId, label: row.sessionId };
}

/**
 * `account.subscription_unowned` — a SHADOWED seat subscription: a second live
 * seat subscription billed beside the one that owns the seats (#7662 v2). Names
 * both; the row carries no tier or seats.
 */
function renderAccountSubscriptionUnowned(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  return {
    line: {
      key: 'terminal.account.subscription_unowned',
      params: {
        subscription: inField(row, 'subscriptionId'),
        owner: inField(row, 'ownerSubscriptionId'),
      },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/** `account.runner_subscription_detached` — names the runner-minute subscription that ended. */
function renderAccountRunnerSubscriptionDetached(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  return {
    line: {
      key: 'terminal.account.runner_subscription_detached',
      params: { subscription: inField(row, 'subscriptionId') },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

/** `billing.invoice_unowned` — names the subscription whose failed invoice did not pause. */
function renderBillingInvoiceUnowned(ctx: TerminalRenderContext): TerminalRendered {
  const { row } = ctx;
  return {
    line: {
      key: 'terminal.billing.invoice_unowned',
      params: { subscription: inField(row, 'subscriptionId') },
    },
    subject: subjectForRow(row),
    refs: { sessionId: row.sessionId },
  };
}

export const TERMINAL_RENDERERS_BILLING_OWNERSHIP = terminalRendererGroup({
  'account.subscription_unowned': {
    level: 'warn',
    actor: 'system',
    render: renderAccountSubscriptionUnowned,
  },
  'account.runner_subscription_detached': {
    level: 'info',
    actor: 'system',
    render: renderAccountRunnerSubscriptionDetached,
  },
  'billing.invoice_unowned': {
    level: 'warn',
    actor: 'system',
    render: renderBillingInvoiceUnowned,
  },
});
